(ns frontend.test.input-checkbox.view
  (:require
   [reagent.core :as r]
   [ui.components.input.checkbox :as checkbox]))

;; The :tw2 style-variant needs a .tw2 ancestor, the :legacy one must NOT sit
;; under it — the scoped Preflight-lite plugin in tailwind.tw2.config.js resets
;; borders and appearance on every `.tw2 *`, which would quietly restyle the
;; legacy checkbox and make the comparison meaningless. So each row renders
;; under its own wrapper rather than the page sharing one.

(def states
  [{:key :unchecked
    :label "Unchecked"
    :value false}
   {:key :checked
    :label "Checked"
    :value true}
   {:key :disabled-unchecked
    :label "Disabled, unchecked"
    :value false
    :disabled? true}
   {:key :disabled-checked
    :label "Disabled, checked"
    :value true
    :disabled? true}])

(defn state-demo
  [{:keys [label value disabled? style-variant]}]
  [:div
   {:class "flex flex-col items-center gap-2 w-32"}
   [checkbox/view
    {:value value
     :disabled? disabled?
     :style-variant style-variant}]
   [:span
    {:class "text-xs text-center"}
    label]])

(defn state-row
  [{:keys [style-variant]}]
  (into
   [:div
    {:class "flex flex-wrap gap-6 items-start"}]
   (doall
    (for [{:keys [key] :as state} states]
      ^{:key key}
      [state-demo (assoc state :style-variant style-variant)]))))

(defn interactive-demo
  [{:keys [label style-variant disabled?]}]
  (r/with-let [checked?* (r/atom false)]
    [:div
     {:class "flex flex-col items-center gap-2 w-32"}
     [checkbox/view
      {:value @checked?*
       :disabled? disabled?
       :style-variant style-variant
       :on-change (fn [new-value]
                    (reset! checked?* new-value))}]
     [:span
      {:class "text-xs text-center"}
      label]
     [:span
      {:class "text-xs text-center"}
      (str ":value " (pr-str @checked?*))]]))

(defn interactive-row
  [{:keys [style-variant]}]
  [:div
   {:class "flex flex-wrap gap-6 items-start"}
   [interactive-demo
    {:label "Toggle"
     :style-variant style-variant}]
   [interactive-demo
    {:label "Toggle, disabled"
     :style-variant style-variant
     :disabled? true}]])

(defn section
  [{:keys [title description]} & children]
  (into
   [:div
    {:class "flex flex-col gap-2"}
    [:h3
     {:class "text-base font-semibold"}
     title]
    [:p
     {:class "text-sm"}
     description]]
   children))

(defn view
  []
  [:div
   {:class "p-4 flex flex-col gap-6 bg-base-light text-base-light-text"}
   [:div
    {:class "flex flex-col gap-2"}
    [:h2
     {:class "text-lg font-semibold"}
     "Checkbox"]
    [:p
     {:class "text-sm"}
     "ui.components.input.checkbox renders two style variants. :legacy is the
      original appearance-none input, :tw2 is the ZapKit checkbox (a visually
      hidden peer input driving a styled box). Both are controlled — :value in,
      :on-change out."]]

   [section
    {:title "Legacy — states"
     :description "style-variant :legacy (the default when the prop is omitted)."}
    [state-row {:style-variant :legacy}]]

   [:div
    {:class "tw2"}
    [section
     {:title "TW2 — states"
      :description "style-variant :tw2. Disabled drops the focus ring and the
                    hover/active shadows."}
     [state-row {:style-variant :tw2}]]]

   [section
    {:title "Legacy — interactive"
     :description "Click to toggle. Tab to the input to see the focus treatment."}
    [interactive-row {:style-variant :legacy}]]

   [:div
    {:class "tw2"}
    [section
     {:title "TW2 — interactive"
      :description "Click to toggle. Tab to the input — the ring is drawn on the
                    box via peer-focus-visible, and its color follows the checked
                    state."}
     [interactive-row {:style-variant :tw2}]]]])
